/**
 * Range2023Strategy
 * Handles '20 to '23 timeline range (2020-2023)
 * Shows content for all tabs with period-type awareness
 * Includes custom table and capitalization sections
 */

import type { ContentStrategy, PeriodType, TabName } from '@/types';
import { DOM_SELECTORS, IMAGE_PATHS } from '@/utils/constants';
import {
  showElements,
  hideElements,
  removeSections,
  createImageHTML,
  injectContent,
  replaceTableHTML,
  hideNoDataMessage,
  showKeyStatsContent,
} from './ContentHelper';

/**
 * Strategy for 2020-2023 timeline range
 */
export class Range2023Strategy implements ContentStrategy {
  private originalTableHTML: string = '';

  /**
   * Apply strategy for 2020-2023 timeline
   */
  public apply(tab: TabName, periodType: PeriodType): void {
    switch (tab) {
      case 'key-stats':
        this.applyKeyStatsContent();
        break;

      case 'income-statement':
        this.applyIncomeStatementContent(periodType);
        break;

      case 'balance-sheet':
        this.applyBalanceSheetContent(periodType);
        break;

      case 'cash-flow':
        this.applyCashFlowContent(periodType);
        break;

      default:
        this.hideAllContent();
    }
  }

  /**
   * Apply Key Stats content: replace table with 2020-2023 data, add capitalization
   */
  private applyKeyStatsContent(): void {
    // Remove all financial statement sections
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Hide any "No data available" message that might be visible
    hideNoDataMessage();

    // Show the original Key Stats content from the view
    showKeyStatsContent();

    // Show key financials sections, hide cap/val
    showElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
    ]);

    hideElements([
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    // Replace table with 2020-2023 data
    if (!this.originalTableHTML) {
      this.originalTableHTML = replaceTableHTML(
        DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
        this.get2020To2023TableHTML()
      );
    }

    // Add 2020-2023 capitalization section
    this.add2020To2023CapitalizationSection();
  }

  /**
   * Apply Income Statement content with period-type awareness
   */
  private applyIncomeStatementContent(periodType: PeriodType): void {
    // Hide standard sections
    hideElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    // Remove other sections
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Remove existing section
    removeSections([DOM_SELECTORS.INCOME_SECTION_2020_2023]);

    // Choose images based on period type
    const imagePaths =
      periodType === 'Quarterly'
        ? IMAGE_PATHS.INCOME_2023_QUARTERLY
        : IMAGE_PATHS.INCOME_2023_ANNUAL;

    const incomeHTML = createImageHTML(
      imagePaths,
      'income-statement-section-2020-2023'
    );

    const success1 = injectContent(DOM_SELECTORS.MAIN_SECTION, incomeHTML);
    if (!success1) {
      console.error('[Range2023Strategy] Failed to inject income statement content');
    }
  }

  /**
   * Apply Balance Sheet content with period-type awareness
   */
  private applyBalanceSheetContent(periodType: PeriodType): void {
    // Hide standard sections
    hideElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    // Remove other sections
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Remove existing section
    removeSections([DOM_SELECTORS.BALANCE_SECTION_2020_2023]);

    // Choose images based on period type
    const imagePaths =
      periodType === 'Quarterly'
        ? IMAGE_PATHS.BALANCE_2023_QUARTERLY
        : IMAGE_PATHS.BALANCE_2023_ANNUAL;

    const balanceHTML = createImageHTML(
      imagePaths,
      'balance-sheet-section-2020-2023'
    );

    const success2 = injectContent(DOM_SELECTORS.MAIN_SECTION, balanceHTML);
    if (!success2) {
      console.error('[Range2023Strategy] Failed to inject balance sheet content');
    }
  }

  /**
   * Apply Cash Flow content with period-type awareness
   */
  private applyCashFlowContent(periodType: PeriodType): void {
    // Hide standard sections
    hideElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    // Remove other sections
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Remove existing section
    removeSections([DOM_SELECTORS.CASH_SECTION_2020_2023]);

    // Choose images based on period type
    const imagePaths =
      periodType === 'Quarterly'
        ? IMAGE_PATHS.CASH_2023_QUARTERLY
        : IMAGE_PATHS.CASH_2023_ANNUAL;

    const cashFlowHTML = createImageHTML(
      imagePaths,
      'cash-flow-section-2020-2023'
    );

    const success3 = injectContent(DOM_SELECTORS.MAIN_SECTION, cashFlowHTML);
    if (!success3) {
      console.error('[Range2023Strategy] Failed to inject cash flow content');
    }
  }

  /**
   * Hide all content for unknown tabs and show "No data available" message
   */
  private hideAllContent(): void {
    hideElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    removeSections([
      DOM_SELECTORS.CAP_SECTION_2020_2023,
      DOM_SELECTORS.VAL_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.INCOME_SECTION_1323,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_1323,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Show "No data available" message
    const mainSection = document.querySelector(DOM_SELECTORS.MAIN_SECTION);
    if (mainSection) {
      mainSection.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; min-height: 400px;">
          <p style="font-size: 16px; color: #666; text-align: center; font-family: 'Times New Roman', Times, serif;">
            No data available for this timeline range and period type.
          </p>
        </div>
      `;
    }
  }

  /**
   * Get the 2020-2023 table HTML
   */
  private get2020To2023TableHTML(): string {
    return `
    <table class="financials-table" style="width:100%; border: 1px solid #ccc;">
      <thead>
        <tr>
          <th scope="col" class="label-column">For the Fiscal Period Ending</th>
          <th scope="col">12 months<br>Jan-31-2021A</th>
          <th scope="col">12 months<br>Jan-30-2022A</th>
          <th scope="col">12 months<br>Jan-29-2023A</th>
        </tr>
        <tr>
          <td class="label-column"><em>Currency</em></td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="label-column">Total Revenue<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Growth Over Prior Year</span></td>
          <td class="number-cell">16,675.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">52.7%</span></td>
          <td class="number-cell">26,914.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">61.4%</span></td>
          <td class="number-cell">26,974.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">0.2%</span></td>
        </tr>
        <tr>
          <td class="label-column">Gross Profit<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Margin %</span></td>
          <td class="number-cell">10,557.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">63.3%</span></td>
          <td class="number-cell">17,475.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">64.9%</span></td>
          <td class="number-cell">15,356.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">56.9%</span></td>
        </tr>
        <tr>
          <td class="label-column">EBITDA<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Margin %</span></td>
          <td class="number-cell">5,819.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">34.9%</span></td>
          <td class="number-cell">11,215.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">41.7%</span></td>
          <td class="number-cell">7,121.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">26.4%</span></td>
        </tr>
        <tr>
          <td class="label-column">EBIT<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Margin %</span></td>
          <td class="number-cell">4,721.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">28.3%</span></td>
          <td class="number-cell">10,041.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">37.3%</span></td>
          <td class="number-cell">5,577.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">20.7%</span></td>
        </tr>
        <tr>
          <td class="label-column">Earnings from Cont. Ops.<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Margin %</span></td>
          <td class="number-cell">4,332.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">26.0%</span></td>
          <td class="number-cell">9,752.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">36.2%</span></td>
          <td class="number-cell">4,368.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">16.2%</span></td>
        </tr>
        <tr>
          <td class="label-column">Net Income<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Margin %</span></td>
          <td class="number-cell">4,332.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">26.0%</span></td>
          <td class="number-cell">9,752.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">36.2%</span></td>
          <td class="number-cell">4,368.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">16.2%</span></td>
        </tr>
        <tr>
          <td class="label-column">Diluted EPS Excl. Extra Items<sup>3</sup><br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Growth Over Prior Year</span></td>
          <td class="number-cell">0.17<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">53.1%</span></td>
          <td class="number-cell">0.39<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">122.5%</span></td>
          <td class="number-cell">0.17<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">(55.8%)</span></td>
        </tr>
      </tbody>
    </table>
  `;
  }

  /**
   * Add 2020-2023 capitalization section after footnote
   */
  private add2020To2023CapitalizationSection(): void {
    const mainContent = document.querySelector(DOM_SELECTORS.MAIN_CONTENT);
    if (!mainContent) return;

    // Remove existing sections if present
    removeSections([
      DOM_SELECTORS.CAP_SECTION_2020_2023,
      DOM_SELECTORS.VAL_SECTION_2020_2023,
    ]);

    // Create capitalization and valuation HTML
    const capitalizationHTML = this.get2020To2023CapitalizationHTML();
    const valuationHTML = this.get2020To2023ValuationHTML();

    // Find footnote and insert after it
    const footnote = document.querySelector(DOM_SELECTORS.FOOTNOTE);
    if (footnote) {
      footnote.insertAdjacentHTML('afterend', capitalizationHTML + valuationHTML);
    }
  }

  /**
   * Get 2020-2023 capitalization HTML (continued in next method due to length)
   */
  private get2020To2023CapitalizationHTML(): string {
    return `
    <div class="capitalization-section capitalization-section-2020-2023" style="margin-top: 20px;">
      <div class="capitalization-header">
        Current Capitalization (Millions of USD)<a href="#" class="blue-link historical-link">View Historical Capitalization</a>
      </div>
      <div class="cap-container">
        <div class="cap-table-col">
          <table style="width:100%; border-collapse:collapse; font-size:12px; color:#333;">
            <tr style="font-style:italic; background:#f0f0f0;">
              <td style="padding:2px 8px; font-style:italic; color:#444; font-weight:bold;">Currency</td>
              <td style="text-align:right; font-style:italic; color:#444; padding:2px 8px; font-weight:bold;">USD</td>
            </tr>
            <tr>
              <td style="padding:2px 8px; color:#444;">Share Price</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">$ 142.50</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">Shares Out.</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">24,650.0</td>
            </tr>
            <tr>
              <td style="font-weight:bold; color:#333; padding:2px 8px;">Market Capitalization</td>
              <td style="text-align:right; font-weight:bold; color:#0056d6; padding:2px 8px;">3,512,625.0</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">- Cash &amp; Short Term Investments</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">21,208.0</td>
            </tr>
            <tr>
              <td style="padding:2px 8px; color:#444;">+ Total Debt</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">8,756.0</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">+ Pref. Equity</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">-</td>
            </tr>
            <tr>
              <td style="padding:2px 8px; color:#444;">+ Total Minority Interest</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">-</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">- Long Term Marketable Securities</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">-</td>
            </tr>
            <tr style="border-top:1px solid #333;">
              <td style="font-weight:bold; color:#333; padding:2px 8px;">= Total Enterprise Value (TEV)</td>
              <td style="text-align:right; font-weight:bold; color:#0056d6; padding:2px 8px;">3,500,173.0</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">Book Value of Common Equity</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">44,537.0</td>
            </tr>
            <tr>
              <td style="padding:2px 8px; color:#444;">+ Pref. Equity</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">-</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">+ Total Minority Interest</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">-</td>
            </tr>
            <tr>
              <td style="padding:2px 8px; color:#444;">+ Total Debt</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">8,756.0</td>
            </tr>
            <tr style="border-top:1px solid #333;">
              <td style="font-weight:bold; color:#333; padding:2px 8px;">= Total Capital</td>
              <td style="text-align:right; font-weight:bold; color:#0056d6; padding:2px 8px;">53,293.0</td>
            </tr>
          </table>
          <div style="margin-top:8px; font-size:11px; color:#555; line-height:1.3;">
            <div>**For companies that have multiple share classes that publicly trade, we are incorporating the different prices to calculate our company level market capitalization. Please click on the value to see the detailed calculation. Prices shown on this page are the close price of the company's primary stock class. Shares shown on this page are total company as-reported share values.</div>
          </div>
        </div>
        <div class="cap-chart-col">
          <img src="../img/cap-chart.png" alt="Capitalization Chart" onerror="this.src='../img/placeholder.png'" style="margin-top: 0px;">
          <div class="chart-legend">
            <div class="legend-items">
              <span class="legend-item"><span class="color-box net-liability"></span>Net Liability</span>
              <span class="legend-item"><span class="color-box total-liability"></span>Total Liability</span>
              <span class="legend-item"><span class="color-box common-equity"></span>Common Equity</span>
              <span class="legend-item"><span class="color-box market-cap"></span>Market Capitalization</span>
            </div>
            <div style="font-size:12px; color:#444; margin-top:4px;">Note: Striped area represents the impact of negative Net Liability on Market Cap.<br>
              Total Liability includes Total Debt, Minority Interest and Pref. Equity.<br>
              Net Liability includes Total Liability, net of Cash and Short Term Investments.<br>
              TEV includes Market Cap and Net Liability.<br>
              Total Capital includes Common Equity and Total Liability.
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
  }

  /**
   * Get 2020-2023 valuation HTML
   */
  private get2020To2023ValuationHTML(): string {
    return `
    <div class="valuation-section valuation-section-2020-2023" style="margin-top:20px; margin-bottom:32px;">
      <div class="capitalization-header" style="width:99%;">
        Valuation Multiples based on Current Capitalization
      </div>
      <div style="width:100%; margin-top:5px;">
        <table style="width:100%; border-collapse:collapse; font-family:Arial, sans-serif; font-size:12px; background:#fafbfc; color:#333;">
          <tr style="background:#e6e6e6;">
            <td style="padding:6px 8px; font-weight:bold; color:#444; font-size:12px;">For the Fiscal Period Ending</td>
            <td style="text-align:center; padding:6px 8px; font-size:12px; font-weight:bold; color:#444;">12 months<br>Jan-26-2025A</td>
            <td style="text-align:center; padding:6px 8px; font-size:12px; font-weight:bold; color:#444;">LTM<br>12 months<br>Apr-27-2025A</td>
            <td style="text-align:center; padding:6px 8px; font-size:12px; font-weight:bold; color:#444;">12 months<br>Jan-31-2026E</td>
            <td style="text-align:center; padding:6px 8px; font-size:12px; font-weight:bold; color:#444;">12 months<br>Jan-31-2027E</td>
            <td style="text-align:center; padding:6px 8px; font-size:12px; font-weight:bold; color:#444;">12 months<br>Jan-31-2028E</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">TEV/Total Revenue</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">29.1x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">25.5x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">19.24x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">15.33x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">13.21x</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">TEV/EBITDA</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">45.5x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">42.8x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">32.52x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">25.72x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">22.27x</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">TEV/EBIT</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">46.7x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">44.0x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">31.04x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">23.33x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">20.35x</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">P/Diluted EPS Before Extra</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">53.5x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">50.7x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">37.13x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">27.64x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">23.97x</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">P/BV</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">48.5x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">45.7x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">26.50x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">16.02x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">10.63x</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">Price/Tang BV</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">52.5x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">49.4x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">-</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">-</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">-</td>
          </tr>
        </table>
      </div>
    </div>
  `;
  }
}
